import React, { useEffect, useLayoutEffect, useState } from "react";
import styled from "styled-components";
import { AlertCircle, Check, CheckCircle } from "react-feather";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { connectorConnectService } from "../../../../services/connectors/connectorConnect.service";
import { connectorConnectionTestApi, connectorSyncApi } from "../../../../services/api";
import { Spinner } from "../../../atoms/Spinner";
import type { StepRenderProps } from "../ConnectorWizardModal.types";

/**
 * TestStep — runs connection test in 2 visual phases.
 *
 *   1. validating  — big spinner + "Validating credentials" + 2 inline loaders
 *   2. connecting  — big spinner + "Connecting to source" + ✓ "Credentials
 *                    validated" (green) + spinner "Connecting to source"
 *   3. success     — green check-in-circle + "Connection successful" +
 *                    "Connected to {connection name}" + ✓ "All checks passed"
 *
 * Phase transitions are now lifecycle-driven by the async test request:
 * enters "connecting" right before API call and completes on response.
 *
 * Forward navigation is gated via `setCanAdvance`: false during phases 1–2,
 * true once phase 3 lands. Back nav stays open the whole time; if the user
 * leaves while loading the cleanup flag stops state updates after unmount.
 */

type TestPhase = "validating" | "connecting" | "success" | "error";
type CheckStatus = "idle" | "loading" | "success" | "error";
type ErrorStage = "credentials" | "source";

/**
 * Turn a raw connector/engine error into a human-friendly message. Providers
 * (esp. Salesforce via Airbyte) surface terse, technical strings; map the
 * common ones to actionable guidance and fall back to the raw text otherwise.
 */
export const toFriendlyTestError = (raw: string | undefined | null): string => {
  const msg = raw ?? "Connection test failed. Please try again.";
  if (
    /REQUEST_LIMIT_EXCEEDED|Request limit reached|TotalRequests Limit exceeded/i.test(
      msg,
    )
  ) {
    return "Salesforce's daily API request limit has been reached. It resets within 24 hours — please try again later.";
  }
  if (/invalid_grant|expired access\/refresh token/i.test(msg)) {
    return "Your Salesforce session has expired. Go Back and reconnect with Salesforce.";
  }
  return msg;
};

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** Matches SelectStep / PreviewStep STEP_MIN_HEIGHT so the modal doesn't
 *  jump heights when navigating Test → Select. */
const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  flex: 1 1 auto;
  min-height: 460px;
  padding: ${spacing[6]} 0;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const Title = styled.p`
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  color: ${rawColors.text.highEmphasis};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

const ChecklistRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const CheckItem = styled.div<{ $tone: "muted" | "success" | "error" }>`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  ${typographyMixin("caption", 3)}
  color: ${({ $tone }) =>
    $tone === "success"
      ? rawColors.accent.green500
      : $tone === "error"
        ? rawColors.accent.red500
        : rawColors.text.mediumEmphasis};
`;

const ErrorMessage = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

const ErrorActions = styled.div`
  display: flex;
  gap: ${spacing[3]};
  margin-top: ${spacing[2]};
`;

const ActionButton = styled.button<{ $variant: "primary" | "outline" }>`
  border-radius: 10px;
  height: 40px;
  min-width: 160px;
  padding: 0 ${spacing[4]};
  ${typographyMixin("body", 3)}
  cursor: pointer;
  border: 1px solid
    ${({ $variant }) =>
      $variant === "primary" ? rawColors.primary[600] : rawColors.primary[500]};
  color: ${({ $variant }) =>
    $variant === "primary"
      ? rawColors.structural.white
      : rawColors.primary[600]};
  background-color: ${({ $variant }) =>
    $variant === "primary"
      ? rawColors.primary[600]
      : rawColors.structural.white};
`;

// ---------------------------------------------------------------------------
// Spinner — wraps the shared Spinner atom with size + color from the design.
// ---------------------------------------------------------------------------

const SpinnerLg = styled(Spinner).attrs({ size: 40 })`
  color: ${rawColors.primary[500]};
`;

const SpinnerSm = styled(Spinner).attrs({ size: 16 })`
  color: ${rawColors.text.lowEmphasis};
`;

// ---------------------------------------------------------------------------
// Success icon — green check inside a soft-green circle (Figma uses
// `accent/green/light` = #DCFCE7 backdrop with the standard check-circle).
// ---------------------------------------------------------------------------

export const SuccessBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[2]};
  border-radius: 9999px;
  background-color: #dcfce7;
  margin-bottom: ${spacing[1]};
`;

export const SuccessIcon = styled(CheckCircle)`
  width: 32px;
  height: 32px;
  color: ${rawColors.accent.green500};
`;

const SmallCheckIcon = styled(Check)`
  width: 16px;
  height: 16px;
  color: ${rawColors.accent.green500};
  stroke-width: 2.5;
`;

const ErrorBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[2]};
  border-radius: 9999px;
  background-color: #fee2e2;
  margin-bottom: ${spacing[1]};
`;

const ErrorIcon = styled(AlertCircle)`
  width: 32px;
  height: 32px;
  color: ${rawColors.accent.red500};
`;

const SmallErrorIcon = styled(AlertCircle)`
  width: 16px;
  height: 16px;
  color: ${rawColors.accent.red500};
  stroke-width: 2.3;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TestStep: React.FC<StepRenderProps> = ({
  values,
  catalogKey,
  connectorSchema,
  apiError,
  setValue,
  setCanAdvance,
  setApiError,
  onRequestBack,
}) => {
  const [phase, setPhase] = useState<TestPhase>("validating");
  const [retryCount, setRetryCount] = useState(0);
  const [errorStage, setErrorStage] = useState<ErrorStage | null>(null);

  /** Synchronously latch the footer button to disabled BEFORE paint. The
   *  wizard hook flips `canAdvance` back to true on every step change (so
   *  non-gated steps work), and a regular useEffect would let one render
   *  through with the button briefly enabled — visible as a flicker on
   *  the Continue button when entering the Test step. useLayoutEffect runs
   *  before the browser paints, so the button starts disabled immediately. */
  useLayoutEffect(() => {
    setCanAdvance(false);
    // Run-once on entry; deps cover only the gating handle, not values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // No ref-based "ran already" guard — the cancelled flag below already
    // makes this StrictMode-safe (the first invocation is cancelled by its
    // own cleanup before the second one even kicks off).
    let cancelled = false;
    setApiError(null);
    setErrorStage(null);
    setCanAdvance(false);
    setPhase("validating");

    const run = async () => {
      let failureStage: ErrorStage | null = null;
      try {
        // OAuth connections (Salesforce) are already persisted server-side with
        // their token — test that EXISTING connection instead of re-submitting
        // the (empty) credentials form, which would wipe the stored token.
        const isOAuthConnection =
          values.__sf_oauth === true &&
          typeof values.connectionId === "string" &&
          values.connectionId.trim().length > 0;

        let ok: boolean;
        let message: string | undefined;
        let connectionId: string | undefined;
        if (isOAuthConnection) {
          const r = await connectorConnectionTestApi.testExisting(
            values.connectionId as string,
          );
          ok = r.success;
          message = r.message ?? undefined;
          connectionId = r.connection_id ?? (values.connectionId as string);
        } else {
          const r = await connectorConnectService.test(
            catalogKey,
            values,
            connectorSchema,
          );
          ok = r.ok;
          message = r.message;
          connectionId = r.connectionId;
        }
        if (cancelled) return;
        if (ok) {
          if (connectionId) {
            setValue("connectionId", connectionId);
          }
          setPhase("connecting");
          try {
            await connectorSyncApi.runNow(catalogKey);
          } catch (error) {
            failureStage = "source";
            throw new Error(
              error instanceof Error && error.message
                ? error.message
                : "Failed to connect source catalog.",
            );
          }
          if (cancelled) return;
          setPhase("success");
          setCanAdvance(true);
        } else {
          failureStage = "credentials";
          setErrorStage("credentials");
          setPhase("error");
          setApiError(toFriendlyTestError(message));
        }
      } catch (err) {
        if (cancelled) return;
        setErrorStage(failureStage ?? "credentials");
        setPhase("error");
        setApiError(
          toFriendlyTestError(err instanceof Error ? err.message : undefined),
        );
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // Run once on mount — values/catalogKey are captured at start time and
    // shouldn't restart the simulation if they update mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  const connectionName =
    typeof values.connection_name === "string" && values.connection_name.trim()
      ? (values.connection_name as string)
      : "your data source";

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  if (phase === "error") {
    return (
      <StatusContainer>
        <ErrorBadge>
          <ErrorIcon aria-hidden="true" />
        </ErrorBadge>
        <Title>Connection failed</Title>
        <ErrorMessage>
          {errorStage === "source"
            ? "Credentials verified, but source connection failed."
            : "We could not verify your credentials."}
        </ErrorMessage>
        {apiError ? (
          <CheckItem $tone="error">
            <SmallErrorIcon aria-hidden="true" />
            <span>{apiError}</span>
          </CheckItem>
        ) : null}
        <ErrorActions>
          <ActionButton type="button" $variant="primary" onClick={handleRetry}>
            Try again
          </ActionButton>
          <ActionButton
            type="button"
            $variant="outline"
            onClick={() => onRequestBack?.()}
          >
            Edit details
          </ActionButton>
        </ErrorActions>
      </StatusContainer>
    );
  }

  if (phase === "success") {
    return (
      <StatusContainer>
        <SuccessBadge>
          <SuccessIcon aria-hidden="true" />
        </SuccessBadge>
        <TitleBlock>
          <Title>Connection successful</Title>
          <Subtitle>Connected to {connectionName}</Subtitle>
        </TitleBlock>
        <CheckItem $tone="success">
          <SmallCheckIcon aria-hidden="true" />
          <span>All checks passed</span>
        </CheckItem>
      </StatusContainer>
    );
  }

  // Loading phases show strictly 2 sequential checks:
  // 1) credentials, 2) source connection.
  const titleText =
    phase === "connecting" ? "Connecting to source" : "Validating credentials";

  const credentialStatus: CheckStatus =
    phase === "validating"
      ? "loading"
      : phase === "connecting"
        ? "success"
        : "idle";
  const connectionStatus: CheckStatus =
    phase === "validating"
      ? "idle"
      : phase === "connecting"
        ? "loading"
        : "idle";

  return (
    <StatusContainer>
      <SpinnerLg aria-hidden="true" />
      <Title>{titleText}</Title>
      <ChecklistRow>
        {credentialStatus === "loading" ? (
          <CheckItem $tone="muted">
            <SpinnerSm aria-hidden="true" />
            <span>Validating credentials</span>
          </CheckItem>
        ) : credentialStatus === "success" ? (
          <CheckItem $tone="success">
            <SmallCheckIcon aria-hidden="true" />
            <span>Credentials validated</span>
          </CheckItem>
        ) : null}

        {connectionStatus === "loading" ? (
          <CheckItem $tone="muted">
            <SpinnerSm aria-hidden="true" />
            <span>Connecting to source</span>
          </CheckItem>
        ) : (
          <CheckItem $tone="muted">
            <span>Connecting to source</span>
          </CheckItem>
        )}
      </ChecklistRow>
    </StatusContainer>
  );
};

export default TestStep;
